// Who may read the org's member roster and its pending invites (app#381).
//
// PERMISSIONS.md § 5 Org: "list members: Admin". The router answers `org_members_list` with
// `forbidden` for a Member (router#1765, action `members.manage`), and after router migration
// `0227` the RLS policies give a Member only their OWN `organization_members` row and no
// `organization_invites` row at all. So the app does not ask for either as a Member: it shows the
// Member their own membership and says who can see the list.
//
// ⚠ This is the SAME gate as `members.manage` in `orgRole.ts`, on purpose. The router gates the
// list with the action that gates the writes, so the two cannot differ here either.
//
// ⚠ It must hold on the router that is live BEFORE router#1765 too, where a Member may still
// list. Not asking is correct on both: the old router would answer, the new one refuses.

import { allows, type OrgRole } from './orgRole.ts'

/**
 * What the members page shows for the caller's role in the active org.
 *
 * - `roster`: the full member list and the pending invites (Owner, Admin).
 * - `self`: the caller's own membership and a sentence that only an Owner or Admin lists
 *   members (Member).
 * - `none`: no role yet (the org list is still loading, or the caller is not a member). Show
 *   nothing rather than a guess.
 */
export type RosterView = 'roster' | 'self' | 'none'

export const mayListMembers = (role: OrgRole): boolean => allows(role, 'members.manage')

export const rosterView = (role: OrgRole): RosterView => {
  if (!role) return 'none'
  return mayListMembers(role) ? 'roster' : 'self'
}

/** The sentence a Member reads in place of the roster. */
export const MEMBER_LIST_NOTICE = 'Only an Owner or an Admin of this organization can see its members and pending invitations.'
